import { createContext, useContext, useEffect, useState } from "react";
import axios from "axios";
import { loadOverrides } from "@/data/rcCodeOverrides";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Empty defaults matching the legacy shape so destructuring never crashes.
const EMPTY_CATALOG = {
  rc: {
    suits: [],
    overcoats: [],
    shirts: [],
    linings: [],
    buttons: { suit: [], overcoat: [], shirt: [] },
  },
  mx: [],
};

const FabricCatalogContext = createContext({
  catalog: EMPTY_CATALOG,
  loaded: false,
  reload: () => {},
});

export function FabricCatalogProvider({ children }) {
  const [catalog, setCatalog] = useState(EMPTY_CATALOG);
  const [loaded, setLoaded] = useState(false);

  const load = async () => {
    try {
      const res = await axios.get(`${API}/fabrics/all`);
      setCatalog(res.data || EMPTY_CATALOG);
      setLoaded(true);
    } catch (e) {
      // Stay with empty catalog; consumers gate on `loaded`.
      setLoaded(true);
    }
  };

  useEffect(() => {
    load();
    // Kick off RC code overrides load in parallel — non-blocking
    loadOverrides();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <FabricCatalogContext.Provider value={{ catalog, loaded, reload: load }}>
      {children}
    </FabricCatalogContext.Provider>
  );
}

/**
 * Returns the full fabric catalog plus a `loaded` flag.
 * Drop-in for legacy `FABRIC_DATA` / `MX_FABRIC_DATA`:
 *   const { FABRIC_DATA, MX_FABRIC_DATA, loaded } = useFabricCatalog();
 */
export function useFabricCatalog() {
  const ctx = useContext(FabricCatalogContext);
  return {
    catalog: ctx.catalog,
    FABRIC_DATA: ctx.catalog.rc,
    MX_FABRIC_DATA: ctx.catalog.mx,
    loaded: ctx.loaded,
    reload: ctx.reload,
  };
}
